import type { PublicKey } from '@solana/web3.js';
import { z } from 'zod';
import { throwHttpError } from '../../core/http-error.js';
import type { MarketEditorialCreateVars } from '../../market-creation/types.js';
import type { ActiveWallet } from '../types.js';

const MARKET_EDITORIAL_CREATE_PATH = '/v1/markets';
const editorialResponseSchema = z.object({
  data: z.object({
    marketPubkey: z.string(),
    outcomes: z.array(
      z.object({
        outcomeIndex: z.number().int().nonnegative(),
        metadataUri: z.string().min(1),
      }),
    ),
  }),
});

interface DecimalStringable {
  toString(radix?: number): string;
}

export interface MarketEditorialChainParams {
  readonly ticker: string;
  readonly outcomes: readonly {
    readonly ticker: string;
  }[];
}

export interface MarketEditorialCreateRequest {
  readonly method: 'POST';
  readonly path: typeof MARKET_EDITORIAL_CREATE_PATH;
  readonly headers: Readonly<Record<string, string>>;
  readonly body: string;
  readonly idempotencyKey: string;
  readonly signal: AbortSignal | undefined;
}

export type MarketEditorialCreateTransport = (
  request: MarketEditorialCreateRequest,
) => Promise<Response>;

export interface PostMarketEditorialCreateArgs {
  readonly payload: MarketEditorialCreateVars;
  readonly activeWallet: ActiveWallet;
  readonly marketNonce: DecimalStringable;
  readonly marketPubkey: string;
  readonly outcomeMints: readonly PublicKey[];
  readonly chainParams: MarketEditorialChainParams;
  readonly transport: MarketEditorialCreateTransport;
  readonly signal: AbortSignal | undefined;
}

export async function postMarketEditorialCreate(
  args: PostMarketEditorialCreateArgs,
): Promise<readonly string[]> {
  assertPayloadMatchesChainParams(args);

  const idempotencyKey = `create-market-editorial:${args.marketPubkey}`;
  const res = await args.transport({
    method: 'POST',
    path: MARKET_EDITORIAL_CREATE_PATH,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
    },
    body: JSON.stringify(args.payload),
    idempotencyKey,
    signal: args.signal,
  });
  if (!res.ok) {
    await throwHttpError('POST', MARKET_EDITORIAL_CREATE_PATH, res);
  }

  const body = (await res.json()) as unknown;
  const parsed = editorialResponseSchema.safeParse(body);
  if (!parsed.success) throw new Error('POST /v1/markets returned invalid pinned outcome metadata');
  if (parsed.data.data.marketPubkey !== args.marketPubkey) {
    throw new Error('POST /v1/markets returned a different market pubkey');
  }
  const outcomes = parsed.data.data.outcomes;
  if (outcomes.length !== args.outcomeMints.length) {
    throw new Error('POST /v1/markets must return pinned metadata for every outcome');
  }
  return outcomes.map((outcome, index) => {
    if (outcome.outcomeIndex !== index) {
      throw new Error('POST /v1/markets returned mismatched outcome metadata indices');
    }
    return outcome.metadataUri;
  });
}

function assertPayloadMatchesChainParams(args: PostMarketEditorialCreateArgs): void {
  const { payload, activeWallet, marketNonce, outcomeMints, chainParams } = args;
  if (payload.creatorPubkey !== activeWallet.address) {
    throw new Error('editorialCreatePayload.creatorPubkey must match the active wallet');
  }
  if (payload.marketNonceDecimal !== marketNonce.toString(10)) {
    throw new Error('editorialCreatePayload.marketNonceDecimal must match marketNonceDecimal');
  }
  if (payload.ticker !== chainParams.ticker) {
    throw new Error('editorialCreatePayload.ticker must match market ticker');
  }
  if (
    payload.outcomes.length !== outcomeMints.length ||
    payload.outcomes.length !== chainParams.outcomes.length
  ) {
    throw new Error('editorialCreatePayload.outcomes length must match outcomes length');
  }
  for (let i = 0; i < outcomeMints.length; i += 1) {
    const editorialOutcome = payload.outcomes[i];
    const outcomeMint = outcomeMints[i];
    const chainOutcome = chainParams.outcomes[i];
    if (editorialOutcome === undefined || outcomeMint === undefined || chainOutcome === undefined) {
      throw new Error(`editorialCreatePayload missing outcome ${String(i)}`);
    }
    if (editorialOutcome.outcomeIndex !== i) {
      throw new Error(`editorialCreatePayload outcome ${String(i)} index does not match`);
    }
    if (editorialOutcome.ticker !== chainOutcome.ticker) {
      throw new Error(`editorialCreatePayload outcome ${String(i)} ticker does not match`);
    }
    const expectedMint = outcomeMint.toBase58();
    if (editorialOutcome.outcomeMintBase58 !== expectedMint) {
      throw new Error(`editorialCreatePayload outcome ${String(i)} mint does not match`);
    }
  }
}
